import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CreateFinancialContactDocument,
  CreateValidatingSalesClientFinancialContactDocument,
  FinanceContactByTaxIdDocument,
  FinanceContactModalDocument,
  FinanceValueObjectsContactTaxIdTypeEnum,
  FinanceValueObjectsContactTypeEnum,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { countries } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ContactTaxIdTypeSelector } from './_ContactTaxIdTypeSelector'

interface FinanceContactModalFormValues {
  name: string
  legalName?: string
  taxId?: string
  taxIdType?: FinanceValueObjectsContactTaxIdTypeEnum
  addressLine1?: string
  addressLine2?: string
  countryCode?: string
  city?: string
  postalCode?: string
  state?: string
}

export const FinanceContactModal = ({
  isOpen,
  onClose,
  refetchQuery,
  name = '',
  contactCreateType,
  validateOnCreate = false,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  name?: string
  contactCreateType: FinanceValueObjectsContactTypeEnum
  validateOnCreate?: boolean
}) => {
  const [checkTaxId, setCheckTaxId] = React.useState<string | undefined>(undefined)
  const [existingContact, setExistingContact] = React.useState<
    { name: string; taxId?: string | null } | undefined
  >(undefined)
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const isClient = contactCreateType === 'client'

  const { data } = useQuery(FinanceContactModalDocument, {
    skip: !isClient,
  })

  const [createFinancialContact] = useMutation(CreateFinancialContactDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceContact,
    refetchQueries: refetchQuery
      ? [refetchQuery, 'FinanceContactsSelector']
      : ['FinanceContactsSelector'],
  })

  const [createValidatingSalesClientFinancialContact] = useMutation(
    CreateValidatingSalesClientFinancialContactDocument,
    {
      extractorFn: (data) => data.financeMutations.createValidatingSalesClientFinanceContact,
      refetchQueries: refetchQuery
        ? [refetchQuery, 'FinanceContactsSelector']
        : ['FinanceContactsSelector'],
    }
  )

  const isAddressFilled = (data: FinanceContactModalFormValues): boolean => {
    const { addressLine1, addressLine2, city, postalCode, state } = data

    return !!(addressLine1 || addressLine2 || city || postalCode || state)
  }

  const canSeeTaxIdType = data?.finance._policies.contactsSeeTaxIdType.can || false
  const showTaxIdTypeSelector = isClient && canSeeTaxIdType

  const commonCreateVariables = (values: FinanceContactModalFormValues) => {
    return {
      name: values.name,
      taxId: values.taxId,
      taxIdType: values.taxIdType,
      legalName: values.legalName,
      address: {
        line1: values.addressLine1 ?? '',
        line2: values.addressLine2 ?? '',
        city: values.city ?? '',
        postalCode: values.postalCode ?? '',
        countryCode: values.countryCode ?? '',
        state: values.state ?? '',
      },
    }
  }

  const handleCreateFinancialContact = async () => {
    const result = await createFinancialContact({
      variables: { ...commonCreateVariables(form.values), contactTypes: [contactCreateType] },
    })
    const handled = handleResult(result, (parsedErrors) => {
      handleParsedErrors(parsedErrors)
    })

    return handled
  }

  const handleCreateValidatingSalesClientFinancialContact = async () => {
    const result = await createValidatingSalesClientFinancialContact({
      variables: commonCreateVariables(form.values),
    })
    const handled = handleResult(result, (parsedErrors) => {
      handleParsedErrors(parsedErrors)
    })

    return handled
  }

  const handleParsedErrors = (parsedErrors: Record<string, string>) => {
    if (
      parsedErrors.name ||
      parsedErrors.taxId ||
      parsedErrors.legalName ||
      parsedErrors.countryCode ||
      parsedErrors.taxIdType ||
      parsedErrors.addressLine1 ||
      parsedErrors.city ||
      parsedErrors.postalCode ||
      parsedErrors.state
    ) {
      form.setErrors({
        name: parsedErrors.name,
        taxId: parsedErrors.taxId,
        legalName: parsedErrors.legalName,
        countryCode: parsedErrors.countryCode,
        taxIdType: parsedErrors.taxIdType,
        addressLine1: parsedErrors.addressLine1,
        city: parsedErrors.city,
        postalCode: parsedErrors.postalCode,
        state: parsedErrors.state,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
      })
    }
  }

  const form = useForm<FinanceContactModalFormValues>({
    defaultValues: {
      taxId: undefined,
      taxIdType: undefined,
      name: name,
      legalName: name,
      addressLine1: undefined,
      addressLine2: undefined,
      city: undefined,
      postalCode: undefined,
      state: undefined,
      countryCode: undefined,
    },
    validateOnChange: true,
    validate: (data) => {
      const errors: FormErrors<FinanceContactModalFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (validateOnCreate) {
        if (showTaxIdTypeSelector && !data.taxIdType) {
          errors.taxIdType = i18n.t('common.validations.empty')
        }
        if (!data.taxId) {
          errors.taxId = i18n.t('common.validations.empty')
        }

        if (!data.countryCode) {
          errors.countryCode = i18n.t('common.validations.empty')
        }
        if (!data.addressLine1) {
          errors.addressLine1 = i18n.t('common.validations.empty')
        }
        if (!data.city) {
          errors.city = i18n.t('common.validations.empty')
        }
        if (!data.postalCode) {
          errors.postalCode = i18n.t('common.validations.empty')
        }
        if (!data.state) {
          errors.state = i18n.t('common.validations.empty')
        }
        if (!data.legalName) {
          errors.legalName = i18n.t('common.validations.empty')
        }
      }

      return errors
    },
    onSubmit: async (values) => {
      const handled = validateOnCreate
        ? await handleCreateValidatingSalesClientFinancialContact()
        : await handleCreateFinancialContact()

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.contacts.activity.create_success'),
          time: 2000,
        })
        userClose()
        return { success: true, data: values }
      } else {
        return {
          errors: [],
          success: false,
        }
      }
    },
  })

  useEffect(() => {
    if (name) {
      form.setFieldValue('name', name)
    }
  }, [name])

  const userClose = () => {
    form.resetForm()
    onClose()
  }

  const addressFilled = isAddressFilled(form.values)

  const error = existingContact
    ? i18n.t('finance.contacts.contact_form.errors.tax_id_uniqueness_error', {
        contact_name: existingContact.name,
      })
    : undefined

  const { data: existingContactData } = useQuery(FinanceContactByTaxIdDocument, {
    variables: { companyId: companyId, taxId: checkTaxId || '' },
    skip: !companyId || !checkTaxId,
  })

  useUnsafeEffect(() => {
    if (existingContactData) {
      const fetchContact = existingContactData.finance.contactsConnection.nodes[0]
      if (fetchContact) {
        setExistingContact(fetchContact)
      } else {
        setExistingContact(undefined)
      }
    }
  }, [existingContactData])

  useEffect(() => {
    if (checkTaxId) {
      setExistingContact(undefined)
    }
  }, [checkTaxId, form.values.name])

  const validateTaxId = (taxId: string) => {
    setCheckTaxId(taxId)
  }

  const debouncedValidation = debounce(validateTaxId, 500)

  if (!isOpen) return null

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('finance.contacts.activity.new_contact_title')}
      description={i18n.t('finance.contacts.activity.new_contact_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={userClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.save')}</Button>
        </>
      }
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.name')}
          value={form.values.name}
          onChange={(value) => form.setFieldValue('name', value)}
          error={form.touched.name ? form.errors.name : undefined}
          mandatory
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.legal_name')}
          value={form.values.legalName}
          onChange={(value) => form.setFieldValue('legalName', value)}
          mandatory={validateOnCreate}
          error={form.touched.legalName ? form.errors.legalName : undefined}
        />
        {showTaxIdTypeSelector && (
          <ContactTaxIdTypeSelector
            selectedTaxIdType={form.values.taxIdType}
            onSelectTaxIdType={(value) => form.setFieldValue('taxIdType', value)}
            error={form.touched.taxIdType ? form.errors.taxIdType : undefined}
            mandatory={validateOnCreate}
          />
        )}
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.tax_identification_number')}
          value={form.values.taxId}
          onChange={(value) => {
            form.setFieldValue('taxId', value)
            debouncedValidation(value)
          }}
          error={error || (form.touched.taxId ? form.errors.taxId : undefined)}
          mandatory={validateOnCreate}
        />
        <Select
          none={i18n.t('common.select')}
          value={form.values.countryCode?.toLowerCase()}
          label={i18n.t('finance.contacts.contact_form.fields.country')}
          options={countries(i18n.locale)}
          onChange={(value) => form.setFieldValue('countryCode', value)}
          error={form.touched.countryCode ? form.errors.countryCode : undefined}
          mandatory={validateOnCreate}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.address_line_1')}
          value={form.values.addressLine1}
          onChange={(value) => form.setFieldValue('addressLine1', value)}
          {...(addressFilled && { mandatory: true })}
          mandatory={validateOnCreate}
          error={form.touched.addressLine1 ? form.errors.addressLine1 : undefined}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.address_line_2')}
          onChange={(value) => form.setFieldValue('addressLine2', value)}
          value={form.values.addressLine2}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.city')}
          value={form.values.city}
          onChange={(value) => form.setFieldValue('city', value)}
          {...(addressFilled && { mandatory: true })}
          mandatory={validateOnCreate}
          error={form.touched.city ? form.errors.city : undefined}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.zip_code')}
          value={form.values.postalCode}
          onChange={(value) => form.setFieldValue('postalCode', value)}
          {...(addressFilled && { mandatory: true })}
          mandatory={validateOnCreate}
          error={form.touched.postalCode ? form.errors.postalCode : undefined}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.state_province_region')}
          value={form.values.state}
          onChange={(value) => form.setFieldValue('state', value)}
          {...(addressFilled && { mandatory: true })}
          mandatory={validateOnCreate}
          error={form.touched.state ? form.errors.state : undefined}
        />
      </Box>
    </Modal>
  )
}
